import { useState } from "react";
import { Input, Button, Checkbox, message, Tag } from "antd";
import { useVerifyCustomerKyc } from "../api/customer";
import { CheckCircleOutlined } from "@ant-design/icons";

type VerifyCustomerProps = {
  id: string;
  isVerified?: boolean;
};

const VerifyCustomer = ({ id, isVerified = false }: VerifyCustomerProps) => {
  const [bankVerification, setBankVerification] = useState(false);
  const [narration, setNarration] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const { mutate: verifyCustomerKyc } = useVerifyCustomerKyc(id);
  const extractMessageFromString = (str: string): string => {
    const match = str.match(/message=([^,}]+)/);
    return match ? match[1].trim() : str;
  };

  const handleVerify = (status: number) => {
    if (!narration.trim()) {
      message.warning("Please enter a narration.");
      return;
    }

    // Set respective button loading state
    if (status === 1) {
      setApproveLoading(true);
    } else {
      setRejectLoading(true);
    }

    verifyCustomerKyc(
      {
        bankVerification,
        status,
        narration,
      },
      {
        onSuccess: () => {
          message.success("Customer verification submitted successfully!");
          setNarration("");
          setBankVerification(false);
        },
        onError: (error: any) => {
          const errorMsg =
            error?.response?.data?.message &&
            typeof error.response.data.message === "string"
              ? extractMessageFromString(error.response.data.message)
              : "Failed to verify customer.";
          message.error(errorMsg);
          setNarration("");
        },
        onSettled: () => {
          setApproveLoading(false);
          setRejectLoading(false);
        },
      }
    );
  };

  if (isVerified) {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircleOutlined className="text-green-600 text-lg" />
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-green-800 font-semibold">Verified</span>
                <Tag
                  color="green"
                  className="border-0 bg-green-100 text-green-700 font-medium"
                >
                  KYF Verified
                </Tag>
              </div>
              <p className="text-green-600 text-sm">
                This customer has been successfully verified through KYF
                process.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 opacity-60 pointer-events-none">
          <div className="flex flex-col gap-4">
            <Checkbox checked={true} disabled>
              <span className="text-gray-600 font-normal">Verified</span>
            </Checkbox>

            <span className="text-gray-500 font-normal">Narration</span>
            <Input
              placeholder="Already verified - no action needed"
              className="w-full md:w-1/2 font-normal"
              value=""
              disabled
            />
          </div>

          <div className="flex gap-4 mt-4">
            <Button
              type="primary"
              disabled
              className="bg-gray-400 border-gray-400"
            >
              Approve
            </Button>
            <Button
              type="default"
              danger
              disabled
              className="border-gray-300 text-gray-400"
            >
              Reject
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        <Checkbox
          checked={bankVerification}
          onChange={(e) => setBankVerification(e.target.checked)}
        >
          <span className="text-gray-700 font-medium">Verify Customer</span>
        </Checkbox>

        <div>
          <span className="text-gray-700 font-medium block mb-2">
            Narration
          </span>
          <Input.TextArea
            placeholder="Enter verification details or remarks..."
            className="w-full md:w-1/2 font-normal"
            value={narration}
            onChange={(e) => setNarration(e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <div className="flex gap-4 mt-4">
        <Button
          type="primary"
          loading={approveLoading}
          onClick={() => handleVerify(1)}
          disabled={!bankVerification || !narration.trim() || approveLoading}
          className="bg-green-600 hover:bg-green-700 border-0 font-medium"
          size="large"
        >
          Approve Verification
        </Button>
        <Button
          type="default"
          danger
          loading={rejectLoading}
          onClick={() => handleVerify(0)}
          disabled={!bankVerification || rejectLoading || !narration.trim()}
          className="!border-gray-300 text-red-600 hover:border-red-400 hover:text-red-700 font-medium"
          size="large"
        >
          Reject Verification
        </Button>
      </div>
    </div>
  );
};

export default VerifyCustomer;
